import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { useGetFgaUserResourcesQuery } from './settingsApiSlice';

export type EntityType = 'advisor' | 'analyst' | 'agent' | 'specialist' | 'action' | 'uxgenie';

export function useEntityResourcePermissions(
  entityType: EntityType,
  resourceId: string | undefined
): { canRead: boolean; canUpdate: boolean; canDelete: boolean; canCreate: boolean } {
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const { data } = useGetFgaUserResourcesQuery(userId || '', {
    skip: !userId,
  });

  return useMemo(() => {
    const resources = data?.data?.resources;
    // Creating is a relation on the entity type, not on a resource. As in PageHeader, a loaded
    // list without entity_types (access management off) allows it; nothing loaded yet does not.
    const entityTypes = resources?.entity_types as Array<{ id?: string; relation: string }> | undefined;
    const canCreate = Boolean(resources) && (
      !Array.isArray(entityTypes)
      || entityTypes.some((r) => r.id === entityType && r.relation === 'create_instance')
    );
    if (!resourceId) {
      return { canRead: false, canUpdate: false, canDelete: false, canCreate };
    }
    const entityResources = resources?.entity_resources as
      | Array<{ entity_type: string; resource_id: string; relation: string }>
      | undefined;
    if (!Array.isArray(entityResources)) {
      return { canRead: false, canUpdate: false, canDelete: false, canCreate };
    }
    const forResource = entityResources.filter(
      (r) => r.entity_type === entityType && r.resource_id === resourceId
    );
    const canRead = forResource.some((r) => r.relation === 'read');
    const canUpdate = forResource.some((r) => r.relation === 'update');
    const canDelete = forResource.some((r) => r.relation === 'delete');
    return { canRead, canUpdate, canDelete, canCreate };
  }, [data, entityType, resourceId]);
}

/**
 * The user's FGA resource list is fetched once and never invalidated, so an entity
 * created during the session has no relations in it and reads as read-only. Screens
 * that create entities call this after the create to pull the list again.
 */
export function useRefreshEntityResourcePermissions(): () => Promise<void> {
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const { refetch } = useGetFgaUserResourcesQuery(userId || '', {
    skip: !userId,
  });

  return useCallback(async () => {
    if (!userId) return;
    try {
      await refetch();
    } catch {
      // Permissions keep their previous values if the refresh fails.
    }
  }, [refetch, userId]);
}
